/**
 * 达人订单详情页 - 顶部 Header + 进度条
 */

import { useNavigate } from "react-router-dom";
import type { StaffOrderDetail } from "@/api/staff";
import type { StepStatus } from "../hooks";
import { getStatusText } from "../hooks";

interface DetailHeaderProps {
  data: StaffOrderDetail;
  serviceSteps: { label: string; status: StepStatus }[];
  payPrice: number;
}

export default function DetailHeader({ data: d, serviceSteps, payPrice }: DetailHeaderProps) {
  const navigate = useNavigate();
  const statusText = getStatusText(d.status);

  return (
    <div
      className="relative overflow-hidden"
      style={{
        background:
          "linear-gradient(145deg,#1A2238 0%,#2D3561 60%,#3D5A80 100%)",
      }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute rounded-full"
          style={{
            width: 200,
            height: 200,
            top: -50,
            right: -30,
            background: "rgba(255,107,53,0.1)",
            filter: "blur(40px)",
          }}
        />
      </div>
      <div className="relative px-4 pb-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
        {/* 标题行 */}
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
            style={{
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <i
              className="ri-arrow-left-line"
              style={{ fontSize: 18, color: "#fff" }}
            />
          </button>
          <div className="flex-1 min-w-0">
            <h1
              className="font-bold text-white leading-snug"
              style={{ fontSize: 15 }}
            >
              {d.city || ""}达人订单
              <span
                className="font-normal ml-1.5"
                style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}
              >
                {" "}
                · 订单号：
              </span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
                {d.ordersn ?? String(d.id)}
              </span>
            </h1>
            <div
              className="mt-0.5"
              style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}
            >
              创建时间：{d.add_time_desc || d.book_time_desc || "-"} | 总金额：
              <span style={{ color: "#FCA5A5", fontWeight: 600 }}>
                ¥{payPrice.toFixed(2)}
              </span>
            </div>
          </div>
          <span
            className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0"
            style={{
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            {statusText}
          </span>
        </div>

        {/* 达人服务进度（6步） */}
        <ServiceSteps steps={serviceSteps} />
      </div>
    </div>
  );
}

/** 进度步骤子组件 */
function ServiceSteps({ steps }: { steps: { label: string; status: StepStatus }[] }) {
  return (
    <div className="mt-3 pt-2 pb-1">
      <div className="flex items-center">
        {steps.map((step, idx) => (
          <div key={idx} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center flex-1">
              <div
                className="flex items-center justify-center rounded-full w-[26px] h-[26px] mb-1"
                style={
                  step.status === "process"
                    ? {
                        background: "#60A5FA",
                        boxShadow:
                          "0 0 0 3px rgba(96,165,250,0.25)",
                      }
                    : step.status === "finish"
                      ? { background: "#3B82F6" }
                      : {
                          background: "rgba(255,255,255,0.08)",
                          border: "1.5px solid rgba(255,255,255,0.15)",
                        }
                }
              >
                {step.status === "finish" ? (
                  <i
                    className="ri-check-line"
                    style={{ fontSize: 11, color: "#fff" }}
                  />
                ) : step.status === "process" ? (
                  <i
                    className="ri-loader-4-line animate-spin"
                    style={{ fontSize: 11, color: "#fff" }}
                  />
                ) : (
                  <span
                    className="text-[9px] font-bold"
                    style={{ color: "rgba(255,255,255,0.35)" }}
                  >
                    {idx + 1}
                  </span>
                )}
              </div>
              <span
                className="text-[9px] leading-tight text-center whitespace-nowrap mt-0.5"
                style={
                  step.status !== "wait"
                    ? { color: "rgba(255,255,255,0.95)", fontWeight: 600 }
                    : { color: "rgba(255,255,255,0.28)" }
                }
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className="flex-1 mx-0.5 mb-[13px]"
                style={{
                  height: 1.5,
                  borderRadius: 2,
                  background:
                    step.status === "finish"
                      ? "rgba(255,255,255,0.55)"
                      : "rgba(255,255,255,0.08)",
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
